import type { RefObject } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';

type UseWidgetWidthIsBelowResult<T extends HTMLElement> = {
  // Note: React refs are nullable at runtime, but MUI component `ref` props are typically typed as `RefObject<T>`.
  // We keep the internal ref nullable and return it as `RefObject<T>` for ergonomic consumption.
  ref: RefObject<T>;
  widthPx: number | null;
  isBelow: boolean;
};

export type WidgetWidthMeasure = 'content-box' | 'border-box';

type Observed<T> = { el: T; measure: WidgetWidthMeasure; ro: ResizeObserver };

function readBorderBoxWidth(entry: ResizeObserverEntry, el: Element): number {
  const sizes = entry.borderBoxSize as
    | ReadonlyArray<ResizeObserverSize>
    | undefined;
  const size = sizes?.[0];
  return size ? size.inlineSize : el.getBoundingClientRect().width;
}

/**
 * Observes a widget container's width and reports whether it's below a given threshold.
 * Intended for DataGrid "listView" decisions when widgets are rendered in narrow columns.
 *
 * `widthPx` is always the content width, whichever box `isBelow` compares.
 */
export function useWidgetWidthIsBelow<T extends HTMLElement = HTMLDivElement>(
  isWidget: boolean,
  thresholdPx: number,
  measure: WidgetWidthMeasure = 'content-box',
): UseWidgetWidthIsBelowResult<T> {
  const ref = useRef<T | null>(null);
  const observedRef = useRef<Observed<T> | null>(null);
  const [widthPx, setWidthPx] = useState<number | null>(null);
  const [comparedWidthPx, setComparedWidthPx] = useState<number | null>(null);

  useEffect(() => {
    if (!isWidget) {
      setWidthPx(null);
      setComparedWidthPx(null);
    }
  }, [isWidget]);

  // No dependency list on purpose: a caller may swap the element the ref points
  // at between renders (a drill-down replaces the measured section, then puts it
  // back). Nothing re-renders on a ref change, so every render checks whether
  // the observed element is still the current one and re-observes if not.
  useEffect(() => {
    if (!isWidget) {
      observedRef.current?.ro.disconnect();
      observedRef.current = null;
      return;
    }

    const el = ref.current;
    if (
      observedRef.current?.el === el &&
      observedRef.current.measure === measure
    ) {
      return;
    }
    observedRef.current?.ro.disconnect();
    observedRef.current = null;

    if (!el || typeof ResizeObserver === 'undefined') {
      return;
    }

    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      // A detached element reports a zero box; keep the last real width.
      if (!entry.target.isConnected) {
        return;
      }
      const nextWidth = entry.contentRect.width;
      if (typeof nextWidth !== 'number' || !Number.isFinite(nextWidth)) {
        return;
      }
      const nextComparedWidth =
        measure === 'border-box' ? readBorderBoxWidth(entry, el) : nextWidth;
      setWidthPx((prev) => (prev === nextWidth ? prev : nextWidth));
      setComparedWidthPx((prev) =>
        prev === nextComparedWidth ? prev : nextComparedWidth,
      );
    });

    ro.observe(el, { box: measure });
    observedRef.current = { el, measure, ro };
  });

  useEffect(
    () => () => {
      observedRef.current?.ro.disconnect();
      observedRef.current = null;
    },
    [],
  );

  const isBelow = useMemo(() => {
    if (!isWidget) return false;
    if (
      typeof comparedWidthPx !== 'number' ||
      !Number.isFinite(comparedWidthPx)
    )
      return false;
    // `<=` aligns with viewport media such as `(max-width: 920px)` at the same breakpoint value.
    return comparedWidthPx > 0 && comparedWidthPx <= thresholdPx;
  }, [isWidget, thresholdPx, comparedWidthPx]);

  return { ref, widthPx, isBelow };
}
